import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// GA4 Measurement ID
const GA_MEASUREMENT_ID = "G-4PRD59BLZ0";

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (
      command: string,
      action: string,
      params?: Record<string, any>
    ) => void;
  }
}

/**
 * Track conversion events for Google Analytics 4
 * Events are structured to be marked as conversions in GA4 dashboard
 */
export const trackConversion = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window.gtag !== "undefined") {
    window.gtag("event", eventName, {
      send_to: GA_MEASUREMENT_ID,
      ...eventParams,
    });
    console.log("GA4 Event:", eventName, eventParams);
  }
};

/**
 * HIGH-VALUE CONVERSION EVENTS
 * Mark these as conversions in GA4: Admin > Events > Mark as conversion
 */

// Quote Request - Primary conversion goal
export const trackQuoteRequest = (source?: string) => {
  trackConversion("generate_lead", {
    event_category: "conversion",
    event_label: source || "quote_form",
    value: 100,
    currency: "EUR",
    lead_type: "quote_request",
  });
  // Also fire custom event for flexibility
  trackConversion("quote_request", {
    event_category: "conversion",
    source: source || "unknown",
    value: 100,
  });
};

// Catalog Download - Lead magnet conversion
export const trackCatalogDownload = (catalogType?: string) => {
  trackConversion("file_download", {
    event_category: "conversion",
    file_name: catalogType || "product_catalog",
    file_extension: "pdf",
    value: 25,
    currency: "EUR",
  });
  // Custom event for detailed tracking
  trackConversion("catalog_download", {
    event_category: "conversion",
    catalog_type: catalogType || "general",
    value: 25,
  });
};

// Contact Form Submit - High-intent conversion
export const trackContactFormSubmit = (formLocation?: string) => {
  trackConversion("generate_lead", {
    event_category: "conversion",
    event_label: formLocation || "contact_page",
    value: 75,
    currency: "EUR",
    lead_type: "contact_form",
  });
  trackConversion("contact_form_submit", {
    event_category: "conversion",
    form_location: formLocation || "contact_page",
    value: 75,
  });
};

// Business Consultation - Highest value B2B conversion
export const trackBusinessConsultation = (source?: string) => {
  trackConversion("generate_lead", {
    event_category: "conversion",
    event_label: source || "b2b_section",
    value: 500,
    currency: "EUR",
    lead_type: "business_consultation",
  });
  trackConversion("business_consultation_request", {
    event_category: "conversion",
    source: source || "b2b_section",
    value: 500,
  });
};

/**
 * ENGAGEMENT EVENTS
 * Track user engagement for funnel analysis
 */

export const trackPhoneClick = () => {
  trackConversion("click", {
    event_category: "engagement",
    link_classes: "phone_click",
    link_text: "Call Button",
    outbound: false,
  });
  trackConversion("phone_click", {
    event_category: "engagement",
    contact_method: "phone",
  });
};

export const trackWhatsAppClick = () => {
  trackConversion("click", {
    event_category: "engagement",
    link_classes: "whatsapp_click",
    link_text: "WhatsApp Button",
    outbound: true,
    link_url: "https://wa.me/34688495309",
  });
  trackConversion("whatsapp_click", {
    event_category: "engagement",
    contact_method: "whatsapp",
  });
};

export const trackProductView = (productName: string, productId?: string) => {
  trackConversion("view_item", {
    event_category: "engagement",
    items: [{
      item_id: productId || productName.toLowerCase().replace(/\s+/g, '_'),
      item_name: productName,
      item_category: "Smart Toilets",
    }],
  });
  trackConversion("product_view", {
    event_category: "engagement",
    product_name: productName,
  });
};

// Email link click
export const trackEmailClick = () => {
  trackConversion("click", {
    event_category: "engagement",
    link_classes: "email_click",
    link_text: "Email Link",
    outbound: false,
  });
  trackConversion("email_click", {
    event_category: "engagement",
    contact_method: "email",
  });
};

// Newsletter signup
export const trackNewsletterSignup = (source?: string) => {
  trackConversion("sign_up", {
    event_category: "conversion",
    method: "newsletter",
    source: source || "footer",
    value: 10,
  });
};

/**
 * Component to track page views on route changes
 */
export const ConversionTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views on route change
    if (typeof window.gtag !== "undefined") {
      window.gtag("config", GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);

  return null;
};

export default ConversionTracking;
